import { config } from '../config';
import { LocaleProvider, Toaster } from '@acast-tech/decibel';
import { useEffect, type ReactNode } from 'react';
import { CreatorHubNav, EchoFooter } from '@acast-tech/echo-navigation';
import { themeConfig } from '@acast-tech/echo-theme';
import { useAuth } from '@acast-tech/auth-client';
import { intercom, request } from '@acast-tech/product-suite-app-utils';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useIntercom } from '@/hooks/useIntercom';
import { CookieConsentProvider } from '@/hooks/useCookieConsent';
import { Internationalization } from '@/i18n/Internationalization';
import { AppConfig } from '@acast-tech/product-suite-app-shell';
import * as Sentry from '@sentry/react';

import ErrorFallback from './ErrorFallback';

interface LayoutProps {
  children: ReactNode;
}

/**
 * Safety-net error boundary for the Echo layout. Catches errors thrown by layout-level
 * components (e.g. CreatorHubNav, CookieConsentProvider, EchoFooter) that are not already handled
 * by the PageWrapper-level boundary. Page-level query errors (403s etc.) are caught lower in the
 * tree and never reach here. Errors are reported to Sentry with the React component stack.
 */
const EchoLayoutErrorBoundary = ({ children }: LayoutProps) => (
  <Sentry.ErrorBoundary
    showDialog={false}
    beforeCapture={(scope, _error, componentStack) => {
      scope.setExtra('componentStack', componentStack);
    }}
    fallback={() => <ErrorFallback />}
  >
    {children}
  </Sentry.ErrorBoundary>
);

/**
 * Echo / Creator Hub navigation chrome (LaunchDarkly `content-performance` flag on).
 */
const EchoNavLayout = ({ children }: LayoutProps) => {
  const { userId, auth0Id, email, avatar, isImpersonating, logout, getToken } = useAuth();
  const { data: rolesData } = useUserRoles();
  useIntercom();
  const rolesRaw = rolesData?.viewer?.roles ?? [];
  const roles = rolesRaw.map((role) => ({
    ...role,
    scopes: {
      networkIds: role.scopes?.networkIds ?? undefined,
      showIds: role.scopes?.showIds ?? undefined,
    },
  }));
  const user = {
    userId: isImpersonating ? (rolesData?.viewer?.id ?? userId) : userId,
    auth0Id,
    email: isImpersonating ? (rolesData?.viewer?.email ?? email) : email,
    avatar: isImpersonating ? null : avatar,
    isImpersonating,
  };

  const handleStopImpersonation = async () => {
    try {
      const token = await getToken();
      await request.delete(`${config.impersonationApiUrl}/v2/sessions`, { token });
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleStartImpersonation = async (impersonationEmail: string) => {
    try {
      const token = await getToken();
      await request.post(`${config.impersonationApiUrl}/v2/sessions`, {
        token,
        data: { email: impersonationEmail },
      });
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppConfig config={{ ...config, name: 'revenue', displayName: 'Revenue' }}>
      <CreatorHubNav
        themeConfig={themeConfig}
        user={user}
        roles={roles}
        onLogoutClick={() => {
          intercom.shutdown();
          logout();
        }}
        onStartImpersonation={handleStartImpersonation}
        onStopImpersonation={handleStopImpersonation}
        environment={config.env === 'production' ? 'prod' : 'dev'}
      >
        <EchoLayoutErrorBoundary>
          <CookieConsentProvider>
            <LocaleProvider locale="en-US">
              <Internationalization>
                <div className="echo-content-reset">{children}</div>
                <EchoFooter
                  termsOfServiceUrl={config.termsOfServiceUrl}
                  privacyPolicyUrl={config.privacyPolicyUrl}
                  themeConfig={themeConfig}
                />
              </Internationalization>
            </LocaleProvider>
          </CookieConsentProvider>
        </EchoLayoutErrorBoundary>
      </CreatorHubNav>
      <Toaster position="bottom left" />
    </AppConfig>
  );
};

const Layout = ({ children }: LayoutProps) => {
  useEffect(() => {
    try {
      if (window.location.host === 'beta.revenue.acast.com') {
        const url = new URL(window.location.href);
        url.host = 'revenue.acast.com';
        window.location.replace(url.href);
      }
    } catch (error) {
      console.error('Redirect failed:', error);
      window.location.replace('https://revenue.acast.com');
    }
  }, []);

  return <EchoNavLayout>{children}</EchoNavLayout>;
};

export default Layout;
